import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "../../utils/formatDate";
import { formatCurrency } from "../../utils/formatCurrency";
import {
  getOverduePayments,
  sendPaymentReminder,
  holdProject,
} from "../../services/paymentService";

const OverduePayments = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = async () => {
    try {
      const data = await getOverduePayments();
      setRows(data);
    } catch {
      setError("Failed to load overdue payments.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRemind = async (row) => {
    if (!window.confirm("Send payment reminder to client?")) return;

    try {
      await sendPaymentReminder({
        projectId: row.project_id,
        pendingAmount: row.pending_amount,
      });
      alert("Payment reminder sent");
    } catch {
      alert("Failed to send reminder");
    }
  };

  const handleHoldProject = async (projectId) => {
    if (!window.confirm("Put this project on payment hold?")) return;

    try {
      await holdProject(projectId);
      alert("Project marked as On-Hold");
      fetchData();
    } catch {
      alert("Failed to update project status");
    }
  };

  if (loading)
    return <div className="p-6 text-center text-gray-600">Loading...</div>;

  if (error)
    return (
      <div className="p-6 text-center text-red-500 font-medium">
        {error}
      </div>
    );

  return (
    <div className="p-0 md:p-2 space-y-6">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h1 className="text-xl font-bold text-gray-800">
          Overdue Projects (Payments)
        </h1>
        <span className="text-sm text-gray-500">
          Grace Period: 2 days
        </span>
      </div>

      {/* EMPTY STATE */}
      {rows.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6 text-center text-gray-500">
          🎉 No overdue projects. You’re all clear.
        </div>
      )}

      {/* ================= MOBILE CARDS ================= */}
      <div className="space-y-4 md:hidden">
        {rows.map((row) => (
          <div
            key={row.project_id}
            className="bg-white rounded-xl shadow-sm border p-4 space-y-3"
          >
            <div>
              <h2 className="font-semibold text-lg">
                {row.project_name}
              </h2>
              <p className="text-sm text-gray-500">
                Client: {row.client_name}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info label="Due Date" value={formatDate(row.earliest_due_date)} />
              <Info
                label="Days Late"
                value={
                  <span className="text-red-600 font-semibold">
                    {row.days_late}
                  </span>
                }
              />
              <Info
                label="Total"
                value={formatCurrency(row.total_expected_amount)}
              />
              <Info
                label="Received"
                value={
                  <span className="text-green-600">
                    {formatCurrency(row.received_amount)}
                  </span>
                }
              />
              <Info
                label="Pending"
                value={
                  <span className="text-red-600 font-semibold">
                    {formatCurrency(row.pending_amount)}
                  </span>
                }
              />
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <button
                onClick={() => handleRemind(row)}
                className="text-blue-600 hover:underline text-sm"
              >
                📤 Remind
              </button>

              <button
                onClick={() => handleHoldProject(row.project_id)}
                className="text-red-600 hover:underline text-sm"
              >
                🔒 Hold
              </button>

              <Link
                to={`/projects/${row.project_id}`}
                className="text-gray-600 hover:underline text-sm"
              >
                👁 View
              </Link>
            </div>
          </div>
        ))}
      </div>

      {/* ================= DESKTOP TABLE ================= */}
      {rows.length > 0 && (
        <div className="hidden md:block overflow-x-auto bg-white rounded-xl shadow-sm border">
          <table className="w-full table-auto text-sm">
            <thead>
              <tr className="bg-gray-800 text-gray-100">
                <th className="p-3 text-left">Project</th>
                <th className="p-3 text-left">Client</th>
                <th className="p-3 text-center">Due Date</th>
                <th className="p-3 text-center">Total</th>
                <th className="p-3 text-center">Received</th>
                <th className="p-3 text-center">Pending</th>
                <th className="p-3 text-center">Days Late</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.project_id}
                  className="border-t hover:bg-slate-50"
                >
                  <td className="p-3 font-medium">
                    {row.project_name}
                  </td>
                  <td className="p-3">{row.client_name}</td>
                  <td className="p-3 text-center">
                    {formatDate(row.earliest_due_date)}
                  </td>
                  <td className="p-3 text-center">
                    {formatCurrency(row.total_expected_amount)}
                  </td>
                  <td className="p-3 text-center text-green-700">
                    {formatCurrency(row.received_amount)}
                  </td>
                  <td className="p-3 text-center text-red-600 font-semibold">
                    {formatCurrency(row.pending_amount)}
                  </td>
                  <td className="p-3 text-center text-red-600">
                    {row.days_late}
                  </td>
                  <td className="p-3 text-center space-x-3">
                    <button
                      onClick={() => handleRemind(row)}
                      className="text-blue-600 hover:underline"
                    >
                      📤 Remind
                    </button>

                    <button
                      onClick={() =>
                        handleHoldProject(row.project_id)
                      }
                      className="text-red-600 hover:underline"
                    >
                      🔒 Hold
                    </button>

                    <Link
                      to={`/projects/${row.project_id}`}
                      className="text-gray-600 hover:underline"
                    >
                      👁 View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ===================== SMALL COMPONENT ===================== */

const Info = ({ label, value }) => (
  <div className="bg-gray-50 rounded-lg p-2">
    <p className="text-xs uppercase tracking-wide text-gray-500">
      {label}
    </p>
    <p className="font-medium text-gray-800">{value}</p>
  </div>
);

export default OverduePayments;
